import {
  Box,
  Button,
  Container,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";

import { getUserEmail, logout } from "./auth";
import { GuideSection, GuideStack, GuideTable } from "./GuideAccordion";
import TroubleshootPage from "./TroubleshootPage";
import { colors } from "./theme";

// Same versioned/-latest alias the admin header uses (slug "apc" baked into the
// filename by scripts/build-installer.ps1). Public URL — no auth needed to GET it.
const INSTALLER_URL = "/admin/downloads/uccx-recorder-helper-apc-installer-latest.exe";

// Shown when the logged-in user is in `helper-agents` (and not supervisor/admin).
// Agents don't use the Search/Agents/Audit/Users console — this is their landing
// spot: grab the installer, read the guide, and self-triage a helper log.
export default function AgentHomeView() {
  const email = getUserEmail();

  return (
    <Container sx={{ pt: 6, pb: 8 }}>
      <Stack spacing={3} sx={{ maxWidth: 820, mx: "auto" }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, color: colors.gray900 }}>
            Agent Screen Recordings
          </Typography>
          <Typography variant="subtitle1" sx={{ color: colors.gray500, mt: 0.5 }}>
            {email ? <>You're set up as a recording agent, <strong>{email}</strong>.</> : "You're set up as a recording agent."}
          </Typography>
        </Box>

        {/* --- Install --- */}
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>Install the recorder on your workstation</Typography>
          <Typography variant="body2" sx={{ color: colors.gray700, mb: 2 }}>
            The recorder runs as a small background app on the PC where you take calls. Download it,
            run it once, and it starts automatically — every time you log in, and right after the install.
          </Typography>

          <Button variant="contained" startIcon={<DownloadIcon />} href={INSTALLER_URL} download>
            Download installer
          </Button>

          <Box component="ol" sx={{ mt: 2, mb: 0, pl: 3, color: colors.gray700, "& li": { mb: 0.75 } }}>
            <li>Run the downloaded <code>.exe</code> on your agent workstation (double-click it; a PowerShell window opens). Windows may warn about an unrecognized app — choose <strong>More info → Run anyway</strong> (the installer isn't code-signed yet).</li>
            <li>When prompted, enter your <strong>Cisco Finesse username and password</strong> — the same ones you use for the agent desktop.</li>
            <li>Then enter your <strong>Recordings-portal password</strong> — the password you set when you signed up here.</li>
            <li>When it says "Done", close that window. The recorder is registered to start at every login, and it's also started right now.</li>
            <li>Within a few seconds you'll see its icon in the system tray (bottom-right of the taskbar) — it shows the current status (idle / recording / uploading). If you don't see it, re-run the installer.</li>
          </Box>

          <Typography variant="caption" sx={{ display: "block", mt: 2, color: colors.gray500 }}>
            Forgot your Recordings-portal password? Sign out, click "Sign in", then "Forgot your password?" on the sign-in page — then re-run the installer so the recorder picks up the new password.
          </Typography>
        </Paper>

        {/* --- Guide --- */}
        <Box>
          <Typography variant="h6" sx={{ mb: 1 }}>Guide</Typography>
          <AgentGuide />
        </Box>

        {/* --- Self-service troubleshoot --- */}
        <Box>
          <Typography variant="h6" sx={{ mb: 1 }}>Something not working?</Typography>
          <Typography variant="body2" sx={{ color: colors.gray700, mb: 2 }}>
            Open the tray icon → "Open log folder", grab the most recent <code>helper-YYYYMMDD.log</code>,
            and paste it below for an instant diagnosis.
          </Typography>
          <TroubleshootPage />
        </Box>

        <Divider />

        <Box>
          <Button variant="outlined" onClick={logout}>Sign out</Button>
        </Box>
      </Stack>
    </Container>
  );
}

// --- The embedded "wiki" guide: collapsible sections, agent-relevant only. ---

function AgentGuide() {
  return (
    <GuideStack>
      <GuideSection title="What gets recorded — and what doesn't" defaultExpanded>
        <p>A small background app on your workstation records <strong>your screen</strong> while you're on a call in Cisco Finesse. When the call ends, the recording is encrypted and uploaded to secure cloud storage where your supervisors can review it.</p>
        <ul>
          <li><strong>No audio</strong> — only the screen is captured. Mic and speaker audio are not recorded by this tool.</li>
          <li><strong>Only during calls</strong> — when you're not on an active call, nothing is captured. Breaks, between-call browsing, idle time — none of it.</li>
          <li><strong>No keylogging or screenshots</strong> — just the screen video for the duration of the call.</li>
          <li>If you have multiple monitors, all connected displays are captured (composited into one video).</li>
        </ul>
      </GuideSection>

      <GuideSection title="The system-tray icon">
        <p>After install you'll see a small <strong>"i" icon</strong> in the system tray (near the clock). That's the recorder. The status updates as you work:</p>
        <GuideTable rows={[
          ["idle", "Alive and waiting for a call."],
          ["recording <call ID>", "A call is active — your screen is being captured."],
          ["uploading <call ID>", "Call ended — encrypting and uploading the recording."],
          ["error: …", "Something went wrong. Right-click → Open log folder for details, or paste the log into the box below."],
        ]} />
        <p style={{ marginTop: 12 }}>Right-click the icon for the menu: <strong>About…</strong> (version + which Finesse server it's connected to), <strong>Open log folder</strong>, and <strong>Quit</strong> (stops the recorder; it restarts at your next login).</p>
      </GuideSection>

      <GuideSection title="Forgot your password?">
        <p><strong>Recordings-portal password:</strong> sign out, click <strong>Sign in</strong>, then <strong>Forgot your password?</strong> on the sign-in form. Enter your email, get the reset code, set a new password — then <strong>re-run the installer</strong> so the recorder picks up the new one (it keeps an encrypted copy locally).</p>
        <p><strong>Finesse password:</strong> that's managed by your IT / Cisco admins, not this tool. After it changes, <strong>re-run the installer</strong> so the recorder has the new one.</p>
      </GuideSection>

      <GuideSection title="Common problems">
        <p>First, try pasting your latest log into the box below — it usually tells you exactly what's wrong. Otherwise:</p>
        <GuideTable rows={[
          ["No tray icon after install", <>The install didn't finish — re-run the installer. If it still doesn't appear, open Task Scheduler, find <code>UccxRecorderHelper</code>, right-click → Run.</>],
          ["\"cognito authentication failed at startup\"", "Your portal password is wrong/expired, or an admin hasn't approved you into helper-agents yet. Check with your admin; reset the password if needed, then re-run the installer."],
          ["\"recording failed: MF_E_INVALIDMEDIATYPE\"", "Windows Server is missing the Server-Media-Foundation feature. Ask IT to add it (Server Manager → Add Roles and Features → Features → Media Foundation) and reboot."],
          ["\"recording failed: BadImageFormatException\"", "Your helper version is out of date — download and run the latest installer."],
          ["Recorder running but nothing shows up", <>No active calls, or the machine can't reach the cloud. Check tray → About — the Finesse host should be shown (not stuck on "(probing)"). If recordings never appear, the machine may not be able to resolve <code>api.apc.intervision-demo.com</code> — ask IT (a DNS issue).</>],
        ]} />
      </GuideSection>

      <GuideSection title="Privacy">
        <ul>
          <li>What's captured: your full screen during active calls — any app you're showing, any browser tab open, anything visible on your monitors.</li>
          <li>Recordings are encrypted both in transit and at rest.</li>
          <li>Supervisors who play back your recordings are <strong>logged</strong> — there's an audit trail of who watched what, when, and (optionally) why.</li>
          <li>Recordings are kept only as long as your organization's retention policy allows; storage is automatically tiered to cheaper archive classes as it ages.</li>
          <li>This tool follows whatever sensitive-data policy your contact center already has — it does not (yet) auto-pause for PCI/credit-card screens.</li>
          <li>If you think you're being recorded when you shouldn't be: right-click the tray icon → Quit, and report it.</li>
        </ul>
      </GuideSection>

      <GuideSection title="Uninstalling">
        <p>The recorder appears in <strong>Settings → Apps → Installed apps</strong> (and the old Control Panel "Programs and Features") as <strong>"Agent Screen Recorder"</strong> — click it → Uninstall. That removes the background task, the program files, and the local config. (IT can also run the bundled <code>uninstall-uccx-helper.ps1</code>.) When you leave the company, your admin disables your account and the recorder stops being able to upload — no action needed on your part.</p>
      </GuideSection>
    </GuideStack>
  );
}
